import { createContext, useEffect, useRef, useState } from 'react';
import EventEmitter from 'events';
import { createHook } from 'utils/utils';

const ee = new EventEmitter();
const EVENT_FRAME_DATA = 'EVENT_PREDICTION';

const CHECK_PORTAL_TIMEOUT = 1000;
const PORTAL_REQUEST_RETRY_TIMEOUT = 100;
// The portal only broadcasts when its own state changes, so re-ask periodically.
// Without this, a snapshot that was incomplete when it arrived stays on screen
// until the user reloads the portal or the content page.
const PORTAL_RESYNC_INTERVAL = 10000;
export const PORTAL_SEARCH_STATUS = {
  SEARCHING: 0,
  FOUND: 1,
  NOT_FOUND: -1,
};

const ChromeExtensionPopupContext = createContext();

const useChromeExtensionPopup = () =>
  createHook('useChromeExtensionPopup', ChromeExtensionPopupContext);

const ChromeExtensionPopupContextProvider = ({ children }) => {
  const [isPaused, setPaused] = useState(null);
  const [intensity, setIntensity] = useState(null);
  const [ambientMovement, setAmbientMovement] = useState(null);
  const [suctionMode, setSuctionMode] = useState(null);
  const [displaySuctionModeSlider, setDisplaySuctionModeSlider] = useState(false);
  const [hidePowerControllSlider, setHidePowerControllSlider] = useState(false);
  const [bytesPerSecond, setBytesPerSecond] = useState(null);
  const [extensionsVersions, setExtensionsVersions] = useState(null);
  const [devices, setDevices] = useState(null);
  const [batteryPerDevice, setBatteryPerDevice] = useState(null);
  const [serverUrl, setServerUrl] = useState(null);
  const [fasterImageCompression, setFasterImageCompression] = useState(false);
  const [lowImageSize, setLowImageSize] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // Flag to indicate that we cannot see the portal open in the other tab
  const [portalStatus, setPortalStatus] = useState(PORTAL_SEARCH_STATUS.SEARCHING);
  // The portal answers UPDATE_REQ with whatever it has at that moment, which
  // during its bootstrap means "not logged in" and "no content URL". Keep
  // showing the loader until it reports a state we can render.
  const [isStateReady, setStateReady] = useState(false);
  // Last values received from the portal, used to avoid echoing them back.
  const lastPortalStateRef = useRef(null);
  const [autopilot, setAutopilot] = useState(false);
  const [testSignal, setTestSignal] = useState(false);
  const [whitelisted, setWhitelisted] = useState(false);

  const [broadcastChannel, setBroadcastChannel] = useState(null);
  const [portalRequestStatusInterval, setPortalRequestStatusInterval] = useState(null);

  const devicesWithSuctionSlider = ['Sam Neo', 'Sam Neo 2', 'Sam Neo 2 Pro'];

  const displayTestSignalSwitch = ['staging.feelme.com', 'localhost:3000'].includes(
    window.location.host,
  );

  // Discover portal presense
  useEffect(() => {
    if (!broadcastChannel) {
      // Broadcast channel is not set yet
      return;
    }

    if (isStateReady) {
      // Portal is detected and reported a state we can render
      return;
    }

    if (portalRequestStatusInterval) {
      // setTimeout it not yet fired, let's wait
      return;
    }

    broadcastChannel.postMessage({ what: 'UPDATE_REQ' });
    const interval = setTimeout(() => {
      // Indicate that it's time to try again
      setPortalRequestStatusInterval(null);
    }, PORTAL_REQUEST_RETRY_TIMEOUT);
    setPortalRequestStatusInterval(interval);
  }, [broadcastChannel, portalRequestStatusInterval, isStateReady]);

  // Don't leave the retry timer running after the popup is closed
  useEffect(() => {
    return () => {
      clearTimeout(portalRequestStatusInterval);
    };
  }, [portalRequestStatusInterval]);

  useEffect(() => {
    // TODO move "extension-popup" and other consts to a common place
    const bc = new BroadcastChannel('extension-popup');
    setBroadcastChannel(bc);
    setTimeout(() => {
      setPortalStatus((oldValue) => {
        // If poratl not found within CHECK_PORTAL_TIMEOUT,
        // indicate it as NOT_FOUND
        return oldValue === PORTAL_SEARCH_STATUS.SEARCHING
          ? PORTAL_SEARCH_STATUS.NOT_FOUND
          : oldValue;
      });
    }, CHECK_PORTAL_TIMEOUT);

    return () => {
      bc.close();
    };
  }, []);

  useEffect(() => {
    if (!broadcastChannel) {
      return;
    }

    broadcastChannel.onmessage = (event) => {
      const { data } = event;
      if (data.what === 'FRAME') {
        ee.emit(EVENT_FRAME_DATA, data);
      } else if (data.what === 'SERVER2POPUP_UPDATE') {
        setPaused(data.isPaused);
        setIntensity(data.intensity);
        setAmbientMovement(autopilot ? 0 : data.ambientMovement);
        setExtensionsVersions(data.extensionsVersions);
        setTestSignal(data.testSignal);
        setDevices(data.devices);
        setBatteryPerDevice(data.batteryPerDevice);
        setServerUrl(data.serverUrl);
        setFasterImageCompression(data.fasterImageCompression);
        setLowImageSize(data.lowImageSize);
        setVideoUrl(data.contentUrl);
        setIsLoggedIn(data.isLoggedIn);
        setPortalStatus(PORTAL_SEARCH_STATUS.FOUND);
        setAutopilot(data.autopilot);
        setWhitelisted(data.whitelisted);
        setSuctionMode(data.suctionMode);
        // Older portal builds don't send `ready` at all — treat those as ready
        // so a stale portal tab can't leave the popup stuck on the loader.
        if (data.ready !== false) {
          setStateReady(true);
        }
        lastPortalStateRef.current = {
          isPaused: data.isPaused,
          intensity: data.intensity,
          ambientMovement: autopilot ? 0 : data.ambientMovement,
          autopilot: data.autopilot,
          suctionMode: data.suctionMode,
          testSignal: data.testSignal,
        };
      } else if (data.what === 'SERVER2POPUP_BYTES_PER_SECOND') {
        setBytesPerSecond(data.bytesPerSecond);
      }
    };

    const last = lastPortalStateRef.current;
    const hasLocalState = isPaused !== null && intensity !== null && ambientMovement !== null;
    const matchesPortalState =
      last &&
      last.isPaused === isPaused &&
      last.intensity === intensity &&
      last.ambientMovement === ambientMovement &&
      last.autopilot === autopilot &&
      last.suctionMode === suctionMode &&
      last.testSignal === testSignal;

    // Don't echo a state we just received back to the portal: it makes the
    // portal re-broadcast, and a re-sync could then overwrite local input.
    if (hasLocalState && !matchesPortalState) {
      broadcastChannel.postMessage({
        what: 'POPUP2SERVER_UPDATE',
        isPaused,
        intensity,
        ambientMovement,
        autopilot,
        suctionMode,
        testSignal,
      });
    }
  }, [isPaused, intensity, ambientMovement, broadcastChannel, autopilot, suctionMode, testSignal]);

  // Safety net: re-ask the portal for its state when the popup regains
  // attention and on a slow heartbeat, so an incomplete or stale snapshot is
  // always repaired instead of waiting for the portal's own state to change.
  useEffect(() => {
    if (!broadcastChannel) {
      return;
    }

    const requestUpdate = () => {
      broadcastChannel.postMessage({ what: 'UPDATE_REQ' });
    };
    const onVisibilityChange = () => {
      if (!document.hidden) {
        requestUpdate();
      }
    };

    const heartbeat = setInterval(requestUpdate, PORTAL_RESYNC_INTERVAL);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', requestUpdate);

    return () => {
      clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', requestUpdate);
    };
  }, [broadcastChannel]);

  // We could simply return last frame message value from this hook,
  // but it doesn't give 30FPS performance.
  // That's why we use subscribe/unsubscribe for synchronous callbacks.
  const subscribeFrameMessages = (callback) => {
    ee.on(EVENT_FRAME_DATA, callback);
    return () => {
      ee.removeListener(EVENT_FRAME_DATA, callback);
    };
  };

  useEffect(() => {
    if (!devices) {
      return;
    }
    setDisplaySuctionModeSlider(false);
    for (let i = 0; i < devices.length; i++) {
      if (devicesWithSuctionSlider.includes(devices[i].name)) {
        setDisplaySuctionModeSlider(true);
      }
    }

    setHidePowerControllSlider(false);
    for (let i = 0; i < devices.length; i++) {
      setHidePowerControllSlider(devices[i]?.removePowerControl);
    }
  }, [devices?.length]);

  return (
    <ChromeExtensionPopupContext.Provider
      value={{
        subscribeFrameMessages,
        hidePowerControllSlider,
        isPaused,
        setPaused,
        intensity,
        setIntensity,
        ambientMovement,
        setAmbientMovement,
        bytesPerSecond,
        extensionsVersions,
        portalStatus,
        isStateReady,
        devices,
        batteryPerDevice,
        serverUrl,
        lowImageSize,
        videoUrl,
        isLoggedIn,
        fasterImageCompression,
        autopilot,
        setAutopilot,
        testSignal,
        setTestSignal,
        whitelisted,
        suctionMode,
        setSuctionMode,
        displaySuctionModeSlider,
        displayTestSignalSwitch,
      }}
    >
      {children}
    </ChromeExtensionPopupContext.Provider>
  );
};

export { ChromeExtensionPopupContextProvider, useChromeExtensionPopup };
